/**
 * ID23 — 이벤트 응모/수집 항목 설정 FE 타입. (be-catalog 계약 — 도착 시 정합.)
 * 결제하기 옵션 모달의 응모폼 필드를 BO 에서 커스텀(CUD)+미리보기한다.
 * 캡처 시점 = 체크아웃. 민감필드(환불계좌/생년월일)는 BE 에서 암호화 저장.
 */

/**
 * 수집 필드 유형. 자유 입력형(TEXT/PHONE/DATE/EMAIL/ACCOUNT)은 maxLength 가 의미를 갖고,
 * 선택형(RADIO/SELECT)은 optionsJson(보기 목록)이 필수다. (be-catalog d2d62f8 / M-5·ID136.)
 */
export type CollectionFieldType =
  | 'TEXT'
  | 'PHONE'
  | 'DATE'
  | 'EMAIL'
  | 'ACCOUNT'
  | 'RADIO'
  | 'SELECT';

export const COLLECTION_FIELD_TYPES: ReadonlyArray<CollectionFieldType> = [
  'TEXT',
  'PHONE',
  'DATE',
  'EMAIL',
  'ACCOUNT',
  'RADIO',
  'SELECT',
];

export const COLLECTION_FIELD_TYPE_LABELS: Record<CollectionFieldType, string> = {
  TEXT: '텍스트',
  PHONE: '전화번호',
  DATE: '날짜',
  EMAIL: '이메일',
  ACCOUNT: '계좌',
  RADIO: '라디오',
  SELECT: '드롭다운',
};

/** 선택형(보기 목록 필수) 여부 — RADIO/SELECT. be-catalog CollectionFieldType.isChoice 와 대응. */
export const isChoiceType = (type: CollectionFieldType): boolean =>
  type === 'RADIO' || type === 'SELECT';

/** 기본 시드 필드 키(신규 이벤트 5종). custom 필드는 임의 키. */
export type CollectionFieldKey = 'NAME' | 'PHONE' | 'BIRTHDATE' | 'EMAIL' | 'REFUND_ACCOUNT' | string;

/** GET .../collection-fields 항목 (EventCollectionFieldResponse). */
export interface EventCollectionField {
  fieldId: string;
  eventId: string;
  /** 항목2 — 소속 몰(BCP-47). be-catalog V101 mall 컬럼. 응답에 동반. */
  mall?: string | null;
  fieldKey: CollectionFieldKey;
  label: string;
  fieldType: CollectionFieldType;
  /** 수집 여부(폼에 노출). */
  collect: boolean;
  /** 필수 입력 여부. */
  required: boolean;
  /** 민감정보 여부(암호화 + 동의 트리거). be-catalog 필드명=sensitive. */
  sensitive: boolean;
  sortOrder: number;
  /** 선택형(RADIO/SELECT) 보기 목록 JSON(문자열 배열). 자유 입력형은 null. */
  optionsJson: string | null;
  /** 자유 입력형 글자수 제한(NULL=무제한). 선택형은 NULL. be-catalog d2d62f8. */
  maxLength: number | null;
}

/** POST/PUT body (EventCollectionFieldRequest). */
export interface EventCollectionFieldPayload {
  /** 항목2 — 소속 몰(BCP-47). 행단위 CRUD 라 각 행이 몰에 귀속(order_form_fields 미러). */
  mall: string;
  fieldKey: CollectionFieldKey;
  label: string;
  fieldType: CollectionFieldType;
  collect: boolean;
  required: boolean;
  sensitive: boolean;
  sortOrder: number;
  optionsJson?: string | null;
  maxLength?: number | null;
}

/**
 * optionsJson(보기 목록 JSON) → 편집용 문자열 배열. 깨진/비배열 값은 빈 배열.
 * be-catalog 와이어는 문자열 배열(`["a","b"]`)이 정본이나, {value,label}[] 도 방어적으로 수용한다
 * (SF normalizeOptions 와 동일 관용). 빈 항목은 제거하지 않고 그대로 두어 BO 편집 중 상태를 보존한다.
 */
export const parseOptions = (optionsJson: string | null | undefined): string[] => {
  if (!optionsJson || optionsJson.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(optionsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((o) => {
        if (typeof o === 'string') return o;
        if (o && typeof o === 'object' && 'value' in o) {
          const v = (o as { value?: unknown }).value;
          return typeof v === 'string' ? v : null;
        }
        return null;
      })
      .filter((v): v is string => v !== null);
  } catch {
    return [];
  }
};

/**
 * 편집용 보기 목록 → optionsJson 문자열. 공백 trim 후 빈 항목 제거.
 * 유효한 보기가 하나도 없으면 null(선택형이면 BO 검증이 저장을 막는다).
 */
export const serializeOptions = (options: ReadonlyArray<string>): string | null => {
  const cleaned = options.map((o) => o.trim()).filter((o) => o.length > 0);
  return cleaned.length > 0 ? JSON.stringify(cleaned) : null;
};

/**
 * maxLength 정규화 — 선택형은 무의미하므로 null, 자유 입력형은 1 이상 양수만 유지(그 외 null=무제한).
 * be-catalog EventCollectionFieldRequest.normalizedMaxLength 와 동일 규칙(BO 선제 정규화).
 */
export const normalizeMaxLength = (
  type: CollectionFieldType,
  maxLength: number | null | undefined,
): number | null => {
  if (isChoiceType(type)) return null;
  return maxLength != null && Number.isFinite(maxLength) && maxLength > 0
    ? Math.floor(maxLength)
    : null;
};
